import { FC, useMemo, useState } from 'react';

import { LmSelectNew } from '@mes-ui/lemma';
import { Box, useMediaQuery } from '@mui/material';
import {
  professionNavigation,
  StatusApplicationCode,
  StatusApplicationCodeText,
  StudentSectionSettingTypeCodes,
} from 'const';
import { useActiveWidget, useAppSelector, useBrightTheme, useWidgetVisibility } from 'hooks';
import { WidgetContainer as Widget } from 'portfolio3/components/common/WidgetContainer';
import { WidgetContainerTitle } from 'portfolio3/components/widgetContainerRecipes';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';
import { Switcher, SwitcherButton } from 'portfolio3/ui-kit';

import { proforientationApplicationSelectors, proforientationVisibilitySelectors } from '../model/selectors';
import { mapStatusToFilterStatus } from '../utils/statusUtils';
import {
  ApplicationExcursionsWidget,
  ApplicationOpenDayWidget,
  ApplicationProbesWidget,
} from './ApplicationSubWidgets';
import { WidgetNames, WidgetType } from './widgets';

interface ApplicationStatusOptionProps {
  label: string;
  value: number;
}

const applicationStatusOptions = [
  {
    value: StatusApplicationCode.approved,
    label: StatusApplicationCodeText[StatusApplicationCode.approved],
  },
  {
    value: StatusApplicationCode.noPlacesAvailable,
    label: StatusApplicationCodeText[StatusApplicationCode.noPlacesAvailable],
  },
  {
    value: StatusApplicationCode.pending,
    label: StatusApplicationCodeText[StatusApplicationCode.pending],
  },
  {
    value: StatusApplicationCode.registered,
    label: StatusApplicationCodeText[StatusApplicationCode.registered],
  },
  {
    value: StatusApplicationCode.canceled,
    label: StatusApplicationCodeText[StatusApplicationCode.canceled],
  },
];

const sectionTypeCode = StudentSectionSettingTypeCodes.profession;
const subsectionTypeCode = StudentSectionSettingTypeCodes.professionApplications;
const navigationData = professionNavigation.applications;

const ProforientationApplicationsWidget: FC = () => {
  const isVisible = useAppSelector(proforientationVisibilitySelectors.applications);
  const probes = useAppSelector(proforientationApplicationSelectors.probes);
  const excursions = useAppSelector(proforientationApplicationSelectors.excursions);
  const dods = useAppSelector(proforientationApplicationSelectors.dods);

  const widgets = [
    {
      id: WidgetType.PROBES,
      isVisible: isVisible.probes,
      component: ApplicationProbesWidget,
    },
    {
      id: WidgetType.EXCURSIONS,
      isVisible: isVisible.excursions,
      component: ApplicationExcursionsWidget,
    },
    {
      id: WidgetType.OPEN_DAY,
      isVisible: isVisible.dods,
      component: ApplicationOpenDayWidget,
    },
  ];

  const { activeWidgetId, setActiveWidgetId, activeWidgets } = useActiveWidget(widgets);
  const [applicationStatus, setApplicationStatus] = useState<ApplicationStatusOptionProps | null>(null);
  const filteredApplicationStatusOptions = useMemo(() => {
    if (activeWidgetId === WidgetType.PROBES) {
      const probesApplicationsIds = Array.from(
        new Set(probes.map((p) => mapStatusToFilterStatus(p.statusApplication))),
      );
      return applicationStatusOptions.filter((a) => probesApplicationsIds.includes(a.value));
    }
    if (activeWidgetId === WidgetType.EXCURSIONS) {
      const excursionsApplicationsIds = Array.from(
        new Set(excursions.map((p) => mapStatusToFilterStatus(p.statusApplication))),
      );
      return applicationStatusOptions.filter((a) => excursionsApplicationsIds.includes(a.value));
    }
    if (activeWidgetId === WidgetType.OPEN_DAY) {
      const dodsApplicationsIds = Array.from(new Set(dods.map((p) => mapStatusToFilterStatus(p.statusApplication))));
      return applicationStatusOptions.filter((a) => dodsApplicationsIds.includes(a.value));
    }
    return [];
  }, [activeWidgetId, probes, excursions, dods]);

  const isSmallScreen = useMediaQuery(commonTheme.breakpoints.down('commonMd'));
  const isBrightTheme = useBrightTheme();
  const { isSettingsMode, isWidgetVisible, toggleElement, overlayElement } = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
  });

  const handleChangeActiveWidget = (widgetType: WidgetType) => {
    setActiveWidgetId(widgetType);
    setApplicationStatus(null);
    emitYMEvent({
      type: 'applications_tab_click',
      payload: {
        tab: WidgetNames[widgetType],
      },
    });
  };

  const widgetTitleElement = (
    <WidgetContainerTitle title={navigationData.label} right={isSettingsMode && toggleElement} />
  );

  const optionsElement = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        flexDirection: isSmallScreen ? 'column' : 'row',
      }}
    >
      <Switcher value={activeWidgetId ?? undefined} onChange={handleChangeActiveWidget} useHiddenList>
        {activeWidgets.map(({ id }) => {
          return <SwitcherButton key={id} value={id} content={WidgetNames[id]} />;
        })}
      </Switcher>

      <Box width="272px">
        <LmSelectNew
          onChange={setApplicationStatus}
          placeholder="Статус заявки"
          value={applicationStatus}
          options={filteredApplicationStatusOptions}
          clearable={true}
          multiple={false}
          grouped={false}
        />
      </Box>
    </Box>
  );

  const applicationStatusCode = applicationStatus ? applicationStatus.value : null;
  const AcitveWidgetComponent = activeWidgets.find((widget) => widget.id === activeWidgetId)?.component;

  if (!isWidgetVisible || activeWidgets.length === 0) return null;

  return (
    <Widget containerData={navigationData} withBackground={isBrightTheme}>
      {overlayElement}
      <Widget.Header title={widgetTitleElement} options={optionsElement} />
      <Widget.Padding>
        {AcitveWidgetComponent && <AcitveWidgetComponent applicationStatusCode={applicationStatusCode} />}
      </Widget.Padding>
    </Widget>
  );
};

export default ProforientationApplicationsWidget;
